/* ==========================================================================
   Global helpers and shared UI components
   ========================================================================== */

(() => {
  const theme = window.theme || (window.theme = {});
  theme.strings = theme.strings || {};

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  theme.prefersReducedMotion = () => reducedMotion.matches;

  theme.debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  theme.format = (template, values) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  theme.announce = (message) => {
    const region = document.getElementById('A11yStatus');
    if (!region || !message) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = message;
    }, 80);
  };

  const subscribers = {};
  theme.subscribe = (event, callback) => {
    (subscribers[event] = subscribers[event] || []).push(callback);
    return () => {
      subscribers[event] = subscribers[event].filter((cb) => cb !== callback);
    };
  };
  theme.publish = (event, data) => {
    (subscribers[event] || []).forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error('[theme] subscriber error', error);
      }
    });
  };

  theme.parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* Dialogs ---------------------------------------------------------------- */

  theme.openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    dialog.dispatchEvent(new CustomEvent('dialog:open'));
  };

  theme.closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    if (dialog.classList.contains('drawer') && !theme.prefersReducedMotion()) {
      dialog.classList.add('is-closing');
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        dialog.classList.remove('is-closing');
        dialog.close();
      };
      dialog.addEventListener('animationend', finish, { once: true });
      window.setTimeout(finish, 400);
    } else {
      dialog.close();
    }
  };

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;
    const openDialogs = Array.from(document.querySelectorAll('dialog[open]'));
    const top = openDialogs[openDialogs.length - 1];
    if (!top) return;
    event.preventDefault();
    theme.closeDialog(top);
  });

  document.addEventListener(
    'cancel',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      event.preventDefault();
      theme.closeDialog(dialog);
    },
    true
  );

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement)) return;
      const opener = dialog._opener;
      if (opener && document.contains(opener) && typeof opener.focus === 'function') {
        opener.focus({ preventScroll: true });
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    const closer = event.target.closest('[data-dialog-close]');
    if (closer) {
      theme.closeDialog(closer.closest('dialog'));
      return;
    }

    if (event.target instanceof HTMLDialogElement && event.target.open && !event.target.classList.contains('lightbox')) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) theme.closeDialog(event.target);
      return;
    }

    const searchOpener = event.target.closest('[data-search-open]');
    if (searchOpener) {
      const modal = document.getElementById('SearchModal');
      if (!modal) return;
      event.preventDefault();
      theme.openDialog(modal, searchOpener);
      const input = modal.querySelector('input[type="search"]');
      if (input) {
        input.focus();
        input.select();
      }
    }
  });

  /* Reveal on scroll ------------------------------------------------------- */

  const revealObserver =
    'IntersectionObserver' in window && document.body && document.body.classList.contains('has-reveal')
      ? new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                entry.target.classList.add('is-visible');
                observer.unobserve(entry.target);
              }
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
        )
      : null;

  theme.initReveal = (root = document) => {
    const elements = root.querySelectorAll('.reveal:not(.is-visible)');
    elements.forEach((element) => {
      if (revealObserver) {
        revealObserver.observe(element);
      } else {
        element.classList.add('is-visible');
      }
    });
  };

  /* Announcement bar ------------------------------------------------------- */

  class AnnouncementSlider extends HTMLElement {
    connectedCallback() {
      this.messages = Array.from(this.querySelectorAll('.announcement-bar__message'));
      if (this.messages.length < 2) return;
      this.index = 0;
      this.delay = (parseInt(this.dataset.autoplay, 10) || 5) * 1000;
      this.querySelectorAll('[data-direction]').forEach((button) => {
        button.addEventListener('click', () => {
          this.go(this.index + (button.dataset.direction === 'next' ? 1 : -1));
          this.restart();
        });
      });
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.addEventListener('focusout', () => this.start());
      this.start();
    }

    disconnectedCallback() {
      this.stop();
    }

    go(index) {
      const count = this.messages.length;
      this.index = (index + count) % count;
      this.messages.forEach((message, i) => {
        const active = i === this.index;
        message.classList.toggle('is-active', active);
        message.setAttribute('aria-hidden', String(!active));
        message.querySelectorAll('a').forEach((link) => {
          link.tabIndex = active ? 0 : -1;
        });
      });
    }

    start() {
      if (this.timer || theme.prefersReducedMotion() || this.messages.length < 2) return;
      this.timer = window.setInterval(() => this.go(this.index + 1), this.delay);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }
  }

  /* Header ---------------------------------------------------------------- */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header');
      this.mode = this.dataset.sticky || 'none';
      this.lastY = window.scrollY;
      this.updateOffset = this.updateOffset.bind(this);
      this.onScroll = this.onScroll.bind(this);
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this.updateOffset);
        this.resizeObserver.observe(this);
      }
      this.updateOffset();
      if (this.mode === 'scroll-up') window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      window.removeEventListener('scroll', this.onScroll);
    }

    updateOffset() {
      const hidden = this.section && this.section.classList.contains('is-hidden');
      const offset = this.mode === 'none' || hidden ? 0 : this.offsetHeight;
      document.documentElement.style.setProperty('--sticky-offset', `${offset}px`);
    }

    onScroll() {
      if (!this.section || document.documentElement.classList.contains('is-locked')) return;
      const y = window.scrollY;
      const goingDown = y > this.lastY;
      this.section.classList.add('is-animating');
      if (goingDown && y > this.offsetHeight + 120) {
        this.section.classList.add('is-hidden');
      } else if (!goingDown) {
        this.section.classList.remove('is-hidden');
      }
      this.lastY = y;
      this.updateOffset();
    }
  }

  const focusableSelector =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      this.panel = this.details.querySelector('.menu-drawer');
      this.summary.setAttribute('aria-expanded', 'false');
      this.details.addEventListener('toggle', () => this.onToggle());
      this.addEventListener('keydown', (event) => this.onKeydown(event));
      this.panel.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) this.close();
      });
      this.mediaQuery = window.matchMedia('(min-width: 990px)');
      this.onBreakpoint = (event) => {
        if (event.matches) this.close();
      };
      this.mediaQuery.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      if (this.mediaQuery) this.mediaQuery.removeEventListener('change', this.onBreakpoint);
      document.documentElement.classList.remove('is-locked');
    }

    onToggle() {
      const open = this.details.open;
      this.summary.setAttribute('aria-expanded', String(open));
      if (open) {
        const header = this.closest('sticky-header') || this.closest('header');
        const bottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        document.documentElement.style.setProperty('--drawer-top', `${bottom}px`);
        document.documentElement.classList.add('is-locked');
        window.requestAnimationFrame(() => {
          const first = this.panel.querySelector(focusableSelector);
          if (first) first.focus();
        });
      } else {
        document.documentElement.classList.remove('is-locked');
      }
    }

    onKeydown(event) {
      if (!this.details.open) return;
      if (event.key === 'Escape') {
        this.close();
        this.summary.focus();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = [this.summary, ...this.panel.querySelectorAll(focusableSelector)].filter(
        (el) => el.offsetParent !== null || el === this.summary
      );
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    close() {
      this.details.open = false;
    }
  }

  class DetailsDropdown extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      if (!this.details) return;
      this.summary = this.details.querySelector('summary');
      this.summary.setAttribute('aria-expanded', 'false');
      this.onOutsideClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this.details.addEventListener('toggle', () => {
        this.summary.setAttribute('aria-expanded', String(this.details.open));
        if (this.details.open) {
          document.querySelectorAll('details-dropdown').forEach((dropdown) => {
            if (dropdown !== this) dropdown.close();
          });
          document.addEventListener('click', this.onOutsideClick);
        } else {
          document.removeEventListener('click', this.onOutsideClick);
        }
      });
      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape' && this.details.open) {
          this.close();
          this.summary.focus();
        }
      });
      this.addEventListener('focusout', (event) => {
        if (this.details.open && event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOutsideClick);
    }

    close() {
      if (this.details) this.details.open = false;
    }
  }

  /* Slider ----------------------------------------------------------------- */

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      if (!this.slides.length) return;
      this.prevButton = this.querySelector('[data-slider-prev]');
      this.nextButton = this.querySelector('[data-slider-next]');
      this.dots = this.querySelector('[data-slider-dots]');
      if (this.prevButton) this.prevButton.addEventListener('click', () => this.step(-1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.step(1));
      this.onScroll = theme.debounce(() => this.update(), 80);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.build());
        this.resizeObserver.observe(this.track);
      }
      this.build();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    metrics() {
      const first = this.slides[0];
      const second = this.slides[1];
      const step = second ? second.offsetLeft - first.offsetLeft : first.offsetWidth || 1;
      const visible = Math.max(1, Math.round(this.track.clientWidth / step));
      return { step, positions: Math.max(1, this.slides.length - visible + 1) };
    }

    build() {
      const { positions } = this.metrics();
      this.toggleAttribute('data-fits', positions <= 1);
      if (this.dots && this.dots.children.length !== positions) {
        this.dots.innerHTML = '';
        for (let i = 0; i < positions; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'slider-dot';
          dot.setAttribute('aria-label', theme.format(theme.strings.slideOf, { index: i + 1, count: positions }));
          dot.addEventListener('click', () => this.goTo(i));
          this.dots.appendChild(dot);
        }
      }
      this.update();
    }

    current() {
      const { step } = this.metrics();
      return Math.round(Math.abs(this.track.scrollLeft) / step);
    }

    goTo(index) {
      const { step, positions } = this.metrics();
      const target = Math.min(positions - 1, Math.max(0, index));
      this.track.scrollTo({ left: target * step, behavior: theme.prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    step(direction) {
      this.goTo(this.current() + direction);
    }

    update() {
      const { positions } = this.metrics();
      const current = Math.min(this.current(), positions - 1);
      if (this.dots) {
        Array.from(this.dots.children).forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
      }
      if (this.prevButton) this.prevButton.disabled = current <= 0;
      if (this.nextButton) this.nextButton.disabled = current >= positions - 1;
    }
  }

  /* Quantity input --------------------------------------------------------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('[data-quantity-step]').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.stepBy(button.dataset.quantityStep === 'up' ? 1 : -1);
        });
      });
      this.input.addEventListener('change', () => this.validate());
      this.input.addEventListener('focus', () => this.input.select());
      this.updateButtons();
    }

    rules() {
      const floor = parseInt(this.input.min, 10) || 0;
      const min = parseInt(this.input.dataset.min, 10) || Math.max(floor, 1);
      const step = parseInt(this.input.step, 10) || 1;
      const max = this.input.max ? parseInt(this.input.max, 10) : null;
      return { floor, min, step, max };
    }

    value() {
      const value = parseInt(this.input.value, 10);
      return Number.isNaN(value) ? 0 : value;
    }

    stepBy(direction) {
      const { floor, min, step, max } = this.rules();
      const current = this.value();
      let next = current + direction * step;
      if (direction > 0 && current < min) next = min;
      if (direction < 0 && next < min) next = floor;
      if (max !== null && next > max) next = current;
      if (next === current) return;
      this.input.value = next;
      this.updateButtons();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    validate() {
      const { floor, min, step, max } = this.rules();
      let value = this.value();
      if (value > 0 || floor > 0) {
        if (value < min) value = min;
        if (step > 1 && value % step !== 0) value = Math.max(min, Math.round(value / step) * step);
        if (max !== null && value > max) value = max - (max % step);
      } else {
        value = floor;
      }
      if (String(value) !== this.input.value) this.input.value = value;
      this.updateButtons();
    }

    updateButtons() {
      const { floor, min, step, max } = this.rules();
      const value = this.value();
      const minus = this.querySelector('[data-quantity-step="down"]');
      const plus = this.querySelector('[data-quantity-step="up"]');
      if (minus) minus.disabled = floor > 0 ? value <= min : value <= 0;
      if (plus) plus.disabled = max !== null && value + step > max;
    }
  }

  /* Localization ------------------------------------------------------------- */

  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      this.querySelectorAll('[data-auto-submit]').forEach((select) => {
        select.addEventListener('change', () => {
          const form = select.closest('form');
          if (form) form.submit();
        });
      });
    }
  }

  /* Deferred video ------------------------------------------------------------ */

  class DeferredVideo extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-video-play]');
      if (this.button) this.button.addEventListener('click', () => this.load(true));
      if (this.dataset.autoplay === 'true' && !theme.prefersReducedMotion() && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.load(false);
              this.observer.disconnect();
            }
          },
          { rootMargin: '200px' }
        );
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    load(focus) {
      if (this.loaded) return;
      const template = this.querySelector('template[data-video-template]');
      if (!template) return;
      const media = template.content.querySelector('video, iframe');
      if (!media) return;
      this.loaded = true;
      const clone = media.cloneNode(true);
      this.appendChild(clone);
      if (this.button) this.button.remove();
      if (clone.tagName === 'VIDEO') {
        const playback = clone.play();
        if (playback && playback.catch) playback.catch(() => {});
      }
      if (focus) clone.focus();
    }
  }

  /* Looping video ------------------------------------------------------------ */

  /* Muted looping video that behaves like a GIF: it only plays while visible,
     stays paused for visitors who prefer reduced motion, and can be paused or
     unmuted with its own buttons. */
  class LoopVideo extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      if (!this.video) return;
      this.video.muted = true;
      this.video.loop = true;
      this.video.playsInline = true;
      this.video.removeAttribute('autoplay');
      this.video.removeAttribute('controls');
      this.toggleButton = this.querySelector('[data-loop-toggle]');
      this.soundButton = this.querySelector('[data-loop-sound]');
      this.userPaused = theme.prefersReducedMotion();
      this.visible = false;

      this.onState = () => this.render();
      this.video.addEventListener('play', this.onState);
      this.video.addEventListener('pause', this.onState);
      this.video.addEventListener('volumechange', this.onState);

      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', () => {
          this.userPaused = !this.video.paused;
          if (this.userPaused) this.video.pause();
          else this.play();
        });
      }
      if (this.soundButton) this.soundButton.addEventListener('click', () => this.toggleSound());

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            this.visible = entries.some((entry) => entry.isIntersecting);
            if (this.visible && !this.userPaused) this.play();
            else if (!this.visible) this.video.pause();
          },
          { threshold: 0.2 }
        );
        this.observer.observe(this);
      } else if (!this.userPaused) {
        this.play();
      }
      this.render();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    play() {
      if (this.video.preload === 'none') this.video.preload = 'auto';
      const playback = this.video.play();
      if (playback && playback.catch) playback.catch(() => this.render());
    }

    toggleSound() {
      const unmute = this.video.muted;
      if (unmute) {
        document.querySelectorAll('loop-video').forEach((other) => {
          if (other !== this && other.video && !other.video.muted) other.video.muted = true;
        });
        this.userPaused = false;
        this.video.muted = false;
        this.play();
      } else {
        this.video.muted = true;
      }
    }

    render() {
      if (!this.video) return;
      const playing = !this.video.paused;
      this.toggleAttribute('data-playing', playing);
      this.toggleAttribute('data-sound', !this.video.muted);
      if (this.toggleButton) {
        this.toggleButton.setAttribute('aria-label', playing ? this.dataset.labelPause : this.dataset.labelPlay);
      }
      if (this.soundButton) {
        this.soundButton.setAttribute('aria-pressed', String(!this.video.muted));
        this.soundButton.setAttribute('aria-label', this.video.muted ? this.dataset.labelUnmute : this.dataset.labelMute);
      }
    }
  }

  /* Share ----------------------------------------------------------------------- */

  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.status = this.querySelector('[data-share-status]');
      if (this.button) this.button.addEventListener('click', () => this.share());
    }

    async share() {
      const data = { url: this.dataset.url, title: this.dataset.title };
      try {
        if (navigator.share) {
          await navigator.share(data);
          return;
        }
        await navigator.clipboard.writeText(data.url);
        if (this.status) this.status.textContent = theme.strings.linkCopied;
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        if (this.status) this.status.textContent = data.url;
      }
    }
  }

  /* Order tracking ------------------------------------------------------------ */

  class TrackOrderForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.error = this.querySelector('[data-track-error]');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        const input = this.form.querySelector('input[name="tracking_number"]');
        const value = input ? input.value.trim() : '';
        if (!value) {
          if (this.error) this.error.hidden = false;
          if (input) {
            input.setAttribute('aria-invalid', 'true');
            input.focus();
          }
          return;
        }
        if (this.error) this.error.hidden = true;
        if (input) input.removeAttribute('aria-invalid');
        const url = (this.dataset.template || '').replace('{number}', encodeURIComponent(value));
        if (/^https?:\/\//i.test(url)) window.open(url, '_blank', 'noopener');
      });
    }
  }

  /* Collection & search filters ----------------------------------------------- */

  class FacetFilters extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.sectionId = this.dataset.section;
      this.debouncedSubmit = theme.debounce(() => this.submit(), 700);
      this.form.addEventListener('change', (event) => {
        if (event.target.type === 'number') {
          this.debouncedSubmit();
        } else {
          this.submit();
        }
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-facet-link]');
        if (!link) return;
        event.preventDefault();
        this.load(link.href);
      });
      this.disclosures = Array.from(this.querySelectorAll('details'));
      this.onOutsideClick = (event) => {
        if (!this.contains(event.target)) this.closeAll();
      };
      this.disclosures.forEach((details) => {
        details.addEventListener('toggle', () => {
          if (!details.open) return;
          this.disclosures.forEach((other) => {
            if (other !== details) other.open = false;
          });
        });
      });
      document.addEventListener('click', this.onOutsideClick);
      this.addEventListener('keyup', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.querySelector('details[open]');
        if (open) {
          open.open = false;
          open.querySelector('summary').focus();
        }
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOutsideClick);
    }

    closeAll() {
      (this.disclosures || []).forEach((details) => {
        details.open = false;
      });
    }

    submit() {
      const params = new URLSearchParams(new FormData(this.form));
      Array.from(params.keys()).forEach((key) => {
        if (params.get(key) === '') params.delete(key);
      });
      const action = this.form.getAttribute('action').split('?')[0];
      this.load(`${action}?${params.toString()}`);
    }

    async load(url) {
      const results = document.getElementById(`ProductGridContainer-${this.sectionId}`);
      const openFacet = this.querySelector('details[open]');
      const openKey = openFacet ? openFacet.dataset.facet : null;
      const activeId = document.activeElement && this.contains(document.activeElement) ? document.activeElement.id : null;
      if (results) results.classList.add('is-loading');
      try {
        const separator = url.includes('?') ? '&' : '?';
        const response = await fetch(`${url}${separator}section_id=${encodeURIComponent(this.sectionId)}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = theme.parseHTML(await response.text());
        const nextResults = doc.getElementById(`ProductGridContainer-${this.sectionId}`);
        const nextFacets = doc.querySelector('facet-filters');
        if (!nextResults || !nextFacets) throw new Error('Missing section markup');
        if (results) results.replaceWith(nextResults);
        this.replaceWith(nextFacets);
        if (openKey) {
          const reopened = nextFacets.querySelector(`details[data-facet="${CSS.escape(openKey)}"]`);
          if (reopened) reopened.open = true;
        }
        if (activeId) {
          const focusTarget = document.getElementById(activeId);
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        }
        window.history.pushState({ facets: true }, '', url);
        lastSearch = window.location.search;
        const count = nextFacets.querySelector('.facets__count');
        if (count) theme.announce(count.textContent.trim());
        theme.initReveal(nextResults);
      } catch (error) {
        console.error('[theme] filtering failed, falling back to page load', error);
        window.location.href = url;
      }
    }
  }

  let lastSearch = window.location.search;
  window.addEventListener('popstate', () => {
    if (window.location.search !== lastSearch && document.querySelector('facet-filters')) {
      window.location.reload();
    }
    lastSearch = window.location.search;
  });

  /* Registration ------------------------------------------------------------- */

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  define('announcement-slider', AnnouncementSlider);
  define('sticky-header', StickyHeader);
  define('menu-drawer', MenuDrawer);
  define('details-dropdown', DetailsDropdown);
  define('slider-component', SliderComponent);
  define('quantity-input', QuantityInput);
  define('localization-form', LocalizationForm);
  define('deferred-video', DeferredVideo);
  define('loop-video', LoopVideo);
  define('share-button', ShareButton);
  define('track-order-form', TrackOrderForm);
  define('facet-filters', FacetFilters);

  /* Page init ------------------------------------------------------------------ */

  const init = () => {
    theme.initReveal();

    if (window.Shopify && window.Shopify.CountryProvinceSelector) {
      document.querySelectorAll('[data-address-form]').forEach((form) => {
        const id = form.dataset.addressForm;
        if (!document.getElementById(`AddressCountry_${id}`)) return;
        new window.Shopify.CountryProvinceSelector(`AddressCountry_${id}`, `AddressProvince_${id}`, {
          hideElement: `AddressProvinceContainer_${id}`,
        });
      });
    }

    document.querySelectorAll('form[data-confirm]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        if (!window.confirm(form.dataset.confirm)) event.preventDefault();
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => theme.initReveal(event.target));
})();
